import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import FeatureCollaborateConversations from "../../Images/Login/feature-collaborate-conversations.png";
import FeatureUnifiedInbox from "../../Images/Login/feature-unified-inbox.png";
import FeatureTeamPerformance from "../../Images/Login/feature-team-performance.png";

// Mirrors Login.jsx's AUTH_COMPACT_HEIGHT_MQ — on a short viewport the fixed chrome around
// this carousel (panel margin/padding, logo row) shrinks, but the image was ALSO capped at a
// fixed percentage of that already-shrunk stage, compounding the squeeze. Loosen the cap on
// short screens so the image fills most of whatever room it's actually given.
const AUTH_COMPACT_HEIGHT_MQ = "@media (max-height:800px)";

const SLIDES = [
  {
    image: FeatureCollaborateConversations,
    title: "Collaborate on key conversations",
    subtitle:
      "Combine all your messaging channels into one and work together efficiently across teams.",
  },
  {
    image: FeatureUnifiedInbox,
    title: "Plan and manage tasks as a team",
    subtitle: "Assign tasks, track deadlines, and stay in sync — all from one collaborative workspace.",
    imageMaxWidth: "96%",
    imageMaxHeight: "90%",
  },
  {
    image: FeatureTeamPerformance,
    title: "Track your team's performance",
    subtitle: "See response times, workloads, and conversation outcomes at a glance.",
  },
];

const AUTO_ADVANCE_MS = 2800;

const fillProgress = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const captionFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SLIDE_TRANSITION_MS = 550;
const SLIDE_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

export const LoginFeatureCarousel = ({ mode = "light" }) => {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((i) => {
    setIndex((prev) => {
      const len = SLIDES.length;
      return ((i % len) + len) % len;
    });
  }, []);

  useEffect(() => {
    if (isHovered) return undefined;
    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timerRef.current);
  }, [isHovered, index]);

  const slide = SLIDES[index];
  const isDark = mode === "dark";

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image stage — same footprint the static hero image used to occupy. All slide images are
          stacked and cross-fade via opacity/scale so transitions are smooth in either direction. */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {SLIDES.map((s, i) => (
          <Box
            key={s.title}
            component="img"
            src={s.image}
            alt={s.title}
            sx={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              maxWidth: s.imageMaxWidth || "82%",
              maxHeight: s.imageMaxHeight || "74%",
              [AUTH_COMPACT_HEIGHT_MQ]: { maxWidth: "90%", maxHeight: "92%" },
              width: "auto",
              height: "auto",
              objectFit: "contain",
              opacity: i === index ? 1 : 0,
              transform: i === index ? "scale(1)" : "scale(0.97)",
              transition: `opacity ${SLIDE_TRANSITION_MS}ms ${SLIDE_EASING}, transform ${SLIDE_TRANSITION_MS}ms ${SLIDE_EASING}`,
              pointerEvents: "none",
            }}
          />
        ))}
      </Box>

      {/* Caption — fades/slides in synced with the image cross-fade */}
      <Box sx={{ textAlign: "center", px: 1, mt: 1, overflow: "hidden" }}>
        <Box
          key={index}
          sx={{
            animation: `${captionFadeIn} ${SLIDE_TRANSITION_MS}ms ${SLIDE_EASING}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Instrument Sans, sans-serif",
              fontWeight: 700,
              fontSize: { md: "1.2rem", lg: "1.35rem" },
              color: isDark ? "#f5f5f5" : "#121a34",
              mb: 0.75,
            }}
          >
            {slide.title}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Instrument Sans, sans-serif",
              fontSize: { md: "0.92rem", lg: "1rem" },
              lineHeight: 1.55,
              color: isDark ? "#9ca3af" : "#6b7280",
            }}
          >
            {slide.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Dots — active one fills like a mini progress bar so it's obvious the slide is auto-advancing */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 2, mb: 0.5, [AUTH_COMPACT_HEIGHT_MQ]: { mt: 1 } }}>
        {SLIDES.map((s, i) =>
          i === index ? (
            <Box
              key={s.title}
              component="button"
              aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
              onClick={() => goTo(i)}
              sx={{
                position: "relative",
                width: 24,
                height: 7,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                p: 0,
                overflow: "hidden",
                backgroundColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(17,24,39,0.15)",
              }}
            >
              <Box
                key={index}
                sx={{
                  position: "absolute",
                  inset: 0,
                  transformOrigin: "left center",
                  backgroundColor: isDark ? "#4FC389" : "#44B57C",
                  animation: `${fillProgress} ${AUTO_ADVANCE_MS}ms linear forwards`,
                  animationPlayState: isHovered ? "paused" : "running",
                }}
              />
            </Box>
          ) : (
            <Box
              key={s.title}
              component="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goTo(i)}
              sx={{
                width: 7,
                height: 7,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                p: 0,
                backgroundColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(17,24,39,0.15)",
                transition: "all 0.25s ease",
              }}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default LoginFeatureCarousel;
